import { NavDrawerRoute, NavGroup } from './models/nav-actions.model';

export interface NavBreadcrumb {
  section: string;
  page: string | null;
}

interface NavLinkMatch {
  route: NavDrawerRoute;
  group?: NavGroup;
  itemTitle: string;
  linkLength: number;
}

export function navTitleToTestId(title: string): string {
  return title.toLowerCase().replaceAll(' ', '-');
}

export function normalizeNavLink(routerLink: string): string {
  const trimmed = routerLink.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function pathMatchesNavLink(currentPath: string, routerLink: string | null | undefined): boolean {
  if (!routerLink) {
    return false;
  }
  return normalizeNavLink(currentPath).startsWith(normalizeNavLink(routerLink));
}

export function isSingleNavLink(route: NavDrawerRoute): boolean {
  return !!route.routerLink && !route.children?.length && !route.drawer?.groups?.length;
}

export function isExpandableNavSection(route: NavDrawerRoute): boolean {
  return (route.children?.length ?? 0) > 0 || (route.drawer?.groups?.length ?? 0) > 0;
}

export function isNavSectionActive(route: NavDrawerRoute, activeTitle: string | null | undefined): boolean {
  if (!activeTitle) {
    return false;
  }
  if (activeTitle === route.title) {
    return true;
  }
  return route.children?.some(child => child.title === activeTitle) ?? false;
}

export function findActiveNavTitle(currentUrl: string, routes: NavDrawerRoute[]): string {
  for (const route of routes) {
    const title = findActiveNavTitleInRoute(currentUrl, route);
    if (title) {
      return title;
    }
  }
  return '';
}

function findActiveNavTitleInRoute(currentUrl: string, route: NavDrawerRoute): string {
  if (route.routerLink && pathMatchesNavLink(currentUrl, route.routerLink)) {
    return route.title;
  }

  for (const child of route.children ?? []) {
    const childTitle = findActiveNavTitleInRoute(currentUrl, child);
    if (childTitle) {
      return childTitle;
    }
  }

  if (
    route.drawer?.groups?.some(group =>
      group.items?.some(item => item.routerLink && pathMatchesNavLink(currentUrl, item.routerLink))
    )
  ) {
    return route.title;
  }

  return '';
}

export function findNavBreadcrumb(currentUrl: string, routes: NavDrawerRoute[]): NavBreadcrumb {
  let best: NavLinkMatch | null = null;

  for (const route of routes) {
    best = pickBetterMatch(best, findBestLinkMatchInRoute(currentUrl, route));
    for (const child of route.children ?? []) {
      best = pickBetterMatch(best, findBestLinkMatchInRoute(currentUrl, child));
    }
  }

  if (!best) {
    return { section: '', page: null };
  }

  const routeTitle = best.route.title.trim();
  const groupTitle = best.group?.title?.trim();
  const section =
    groupTitle && groupTitle.toLowerCase() !== routeTitle.toLowerCase()
      ? `${best.route.title}: ${groupTitle}`
      : best.route.title;
  const page = best.itemTitle !== section ? best.itemTitle : null;

  return { section, page };
}

function findBestLinkMatchInRoute(currentUrl: string, route: NavDrawerRoute): NavLinkMatch | null {
  let best: NavLinkMatch | null = null;

  if (route.routerLink && pathMatchesNavLink(currentUrl, route.routerLink)) {
    best = {
      route,
      itemTitle: route.title,
      linkLength: normalizeNavLink(route.routerLink).length,
    };
  }

  for (const group of route.drawer?.groups ?? []) {
    for (const item of group.items) {
      if (item.routerLink && pathMatchesNavLink(currentUrl, item.routerLink)) {
        best = pickBetterMatch(best, {
          route,
          group,
          itemTitle: item.title,
          linkLength: normalizeNavLink(item.routerLink).length,
        });
      }
    }
  }

  return best;
}

function pickBetterMatch(current: NavLinkMatch | null, candidate: NavLinkMatch | null): NavLinkMatch | null {
  if (!candidate) {
    return current;
  }
  if (!current || candidate.linkLength > current.linkLength) {
    return candidate;
  }
  return current;
}

export function collectAutoExpandedNavTitles(currentUrl: string, routes: NavDrawerRoute[]): Set<string> {
  const expanded = new Set<string>();

  for (const route of routes) {
    if (route.children?.length) {
      expanded.add(route.title);
      for (const child of route.children) {
        if (findActiveNavTitleInRoute(currentUrl, child)) {
          expanded.add(child.title);
        }
      }
    } else if (findActiveNavTitleInRoute(currentUrl, route)) {
      expanded.add(route.title);
    }
  }

  return expanded;
}
